import { lazy, Suspense, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider } from '@/features/auth/AuthContext'
import { ProtectedRoute } from '@/features/auth/ProtectedRoute'
import { ErrorBoundary } from '@/components/common/ErrorBoundary'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { useUIStore } from '@/stores'

// Lazy-loaded pages for code splitting
const LoginPage = lazy(() => import('@/pages/LoginPage').then(m => ({ default: m.LoginPage })))
const DashboardPage = lazy(() => import('@/pages/DashboardPage').then(m => ({ default: m.DashboardPage })))
const TrainPage = lazy(() => import('@/pages/TrainPage').then(m => ({ default: m.TrainPage })))
const ProgressPage = lazy(() => import('@/pages/ProgressPage').then(m => ({ default: m.ProgressPage })))
const SettingsPage = lazy(() => import('@/pages/SettingsPage').then(m => ({ default: m.SettingsPage })))
const ExercisesPage = lazy(() => import('@/pages/ExercisesPage').then(m => ({ default: m.ExercisesPage })))
const ExercisePlayerPage = lazy(() => import('@/pages/ExercisePlayerPage').then(m => ({ default: m.ExercisePlayerPage })))
const ConversationsPage = lazy(() => import('@/pages/ConversationsPage').then(m => ({ default: m.ConversationsPage })))
const ScenariosPage = lazy(() => import('@/pages/ScenariosPage').then(m => ({ default: m.ScenariosPage })))
const UnauthorizedPage = lazy(() => import('@/pages/UnauthorizedPage').then(m => ({ default: m.UnauthorizedPage })))
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage').then(m => ({ default: m.NotFoundPage })))
const PricingPage = lazy(() => import('@/pages/PricingPage').then(m => ({ default: m.PricingPage })))
const BillingSuccessPage = lazy(() => import('@/pages/BillingSuccessPage').then(m => ({ default: m.BillingSuccessPage })))

// Admin pages
const AdminLayout = lazy(() => import('@/components/layout/AdminLayout').then(m => ({ default: m.AdminLayout })))
const ExerciseManagement = lazy(() => import('@/features/admin/pages/ExerciseManagement').then(m => ({ default: m.ExerciseManagement })))
const AnalyticsDashboard = lazy(() => import('@/features/admin/pages/AnalyticsDashboard').then(m => ({ default: m.AnalyticsDashboard })))
const UserManagement = lazy(() => import('@/features/admin/pages/UserManagement').then(m => ({ default: m.UserManagement })))
const AuditLogPage = lazy(() => import('@/features/admin/pages/AuditLogPage').then(m => ({ default: m.AuditLogPage })))
const AssignmentsPage = lazy(() => import('@/features/admin/pages/AssignmentsPage').then(m => ({ default: m.AssignmentsPage })))
const KnowledgeBasePage = lazy(() => import('@/features/admin/pages/KnowledgeBasePage').then(m => ({ default: m.KnowledgeBasePage })))
const BillingManagement = lazy(() => import('@/features/admin/pages/BillingManagement').then(m => ({ default: m.BillingManagement })))
const LMSPage = lazy(() => import('@/features/admin/pages/LMSPage').then(m => ({ default: m.LMSPage })))

function App() {
  const theme = useUIStore((state) => state.theme)

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement
    if (theme === 'dark') {
      root.classList.add('dark')
    } else {
      root.classList.remove('dark')
    }
  }, [theme])

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<LoadingSpinner fullScreen />}>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/pricing" element={<PricingPage />} />

              {/* Root redirect */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />

              {/* Protected trainee routes - accessible to all authenticated users */}
              <Route element={<ProtectedRoute />}>
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/train" element={<TrainPage />} />
                <Route path="/progress" element={<ProgressPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                {/* Legacy routes still work */}
                <Route path="/exercises" element={<ExercisesPage />} />
                <Route path="/exercises/:id" element={<ExercisePlayerPage />} />
                <Route path="/conversations" element={<ConversationsPage />} />
                <Route path="/scenarios" element={<ScenariosPage />} />
                <Route path="/billing/success" element={<BillingSuccessPage />} />
              </Route>

              {/* Protected admin routes - only for admin and instructor roles */}
              <Route element={<ProtectedRoute allowedRoles={['admin', 'instructor']} />}>
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<Navigate to="/admin/analytics" replace />} />
                  <Route path="exercises" element={<ExerciseManagement />} />
                  <Route path="analytics" element={<AnalyticsDashboard />} />
                  <Route path="users" element={<UserManagement />} />
                  <Route path="audit" element={<AuditLogPage />} />
                  <Route path="assignments" element={<AssignmentsPage />} />
                  <Route path="knowledge-base" element={<KnowledgeBasePage />} />
                  <Route path="billing" element={<BillingManagement />} />
                  <Route path="lms" element={<LMSPage />} />
                </Route>
              </Route>

              {/* Error pages */}
              <Route path="/unauthorized" element={<UnauthorizedPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  )
}

export default App
